import React, { useEffect, useRef, useState } from 'react';
import './Model3Section.css';
import backgroundImage from '../assets/6781a3eddbe04f983742a87c_model-3-backgrounod.jpg';

const Model3Section = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Set animation complete after all cards finish animating (1.2s delay + 0.8s animation)
          setTimeout(() => {
            setAnimationComplete(true);
            // Dispatch custom event to notify next section
            window.dispatchEvent(new CustomEvent('model3AnimationComplete'));
          }, 2000);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '-50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section className="model3-section" ref={sectionRef}>
      <div className="model3-background">
        <div className={`model3-container ${isVisible ? 'animate-up' : ''}`}>
          <div className="model3-header">
            <div className="model3-logo">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#00f0ff" />
                    <stop offset="50%" stopColor="#ffffff" />
                    <stop offset="100%" stopColor="#ff6b6b" />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge> 
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Outer ring */}
                <circle cx="40" cy="40" r="35" stroke="url(#logoGradient)" strokeWidth="2" fill="none" filter="url(#glow)" opacity="0.8"/>
                
                {/* Inner geometric pattern */}
                <path d="M25 25 L55 25 L55 55 L25 55 Z" stroke="url(#logoGradient)" strokeWidth="1.5" fill="none" opacity="0.6"/>
                <path d="M30 30 L50 30 L50 50 L30 50 Z" stroke="url(#logoGradient)" strokeWidth="1" fill="none" opacity="0.4"/>
                
                {/* Central design element */}
                <circle cx="40" cy="40" r="8" fill="url(#logoGradient)" opacity="0.8"/>
                
                {/* Connecting dots */}
                <circle cx="15" cy="40" r="2" fill="url(#logoGradient)" opacity="0.7"/>
                <circle cx="65" cy="40" r="2" fill="url(#logoGradient)" opacity="0.7"/>
                <circle cx="40" cy="15" r="2" fill="url(#logoGradient)" opacity="0.7"/>
                <circle cx="40" cy="65" r="2" fill="url(#logoGradient)" opacity="0.7"/>
                
                {/* Connection lines */}
                <line x1="17" y1="40" x2="23" y2="40" stroke="url(#logoGradient)" strokeWidth="1" opacity="0.5"/>
                <line x1="57" y1="40" x2="63" y2="40" stroke="url(#logoGradient)" strokeWidth="1" opacity="0.5"/>
                <line x1="40" y1="17" x2="40" y2="23" stroke="url(#logoGradient)" strokeWidth="1" opacity="0.5"/>
                <line x1="40" y1="57" x2="40" y2="63" stroke="url(#logoGradient)" strokeWidth="1" opacity="0.5"/>
              </svg>
            </div>
            <h1 className="model3-title">Crafting with Code & AI</h1>
            <h2 className="model3-subtitle">
              Blending creativity, intelligence, and technology to build impactful solutions
            </h2>
          </div>
          
          <div className="model3-features">
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h3 className="feature-title">AI & Machine Learning</h3>
              <p className="feature-description">
                Design and develop intelligent models for real-world tasks such as image recognition, NLP, and predictive systems.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h3 className="feature-title">Frontend Development</h3>
              <p className="feature-description">
                Create modern, responsive, and interactive web applications using React, Next.js, Tailwind CSS, and Framer Motion.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3v18h18" stroke="currentColor" strokeWidth="2"/>
                  <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h3 className="feature-title">Data Visualization</h3>
              <p className="feature-description">
                Transform complex datasets into clear, interactive dashboards and meaningful insights with Python, Power BI, or Tableau.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                  <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" strokeWidth="2"/>
                  <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h3 className="feature-title">Custom Web Applications</h3>
              <p className="feature-description">
                Build scalable and user-focused applications tailored to unique requirements with seamless performance.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h3 className="feature-title">UI/UX Design Enhancements</h3>
              <p className="feature-description">
                Develop engaging and accessible interfaces that balance creativity with usability for better user experiences.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h3 className="feature-title">Collaboration & Problem Solving</h3>
              <p className="feature-description">
                Work effectively in teams, embracing agile practices, innovation, and shared success to deliver impactful results.
              </p>
            </div>
          </div>
          
          <div className="model3-cta">
            <button 
              className="model3-btn"
              onClick={() => {
                window.location.href = '/projects';
              }}
            >
              Explore My Projects
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Model3Section;
